import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, shareReplay, tap } from "rxjs";
import { BackendHealth, HealthState } from "../models/health-status.model";
import { HttpClient, HttpContext } from "@angular/common/http";
import { BACKENDS_CONFIG } from "../configs/backend-health-config.config";
import { SKIP_LOADER } from "../interceptors/skip-loader.context";

@Injectable({ providedIn: 'root' })
export class HealthService {
    private http = inject(HttpClient);

    private readonly healthSubject = new BehaviorSubject<BackendHealth[]>(BACKENDS_CONFIG);
    readonly health$ = this.healthSubject.asObservable();

    private init$?: Observable<BackendHealth[]>;

    checkAllOnce(): Observable<BackendHealth[]> {
        if (!this.init$) {
            this.init$ = this.doCheckAll().pipe(shareReplay(1));
        }
        return this.init$;
    }

    private doCheckAll(): Observable<BackendHealth[]> {
        const silentContext = new HttpContext().set(SKIP_LOADER, true);

        const requests = this.healthSubject.value.map(b =>
            this.http.get(b.url, { 
                observe: 'response',
                context: silentContext
            }).pipe(
                map(res => ({
                    ...b,
                    state: this.mapStatus(res.status),
                    lastChecked: new Date(),
                    error: undefined
                }) as BackendHealth),
                catchError(err =>
                    of({
                        ...b,
                        state: 'unhealthy' as HealthState,
                        lastChecked: new Date(),
                        error: err
                    } as BackendHealth)
                )
            )
        );

        return forkJoin(requests).pipe(
            tap(results => this.healthSubject.next(results))
        );
    }

    private mapStatus(status: number): HealthState {
        return status >= 200 && status < 300 ? 'healthy' : 'unhealthy';
    }
}